import React from "react";
import App from "./App";
import { Provider } from "react-redux";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { baseStore } from "./redux/store";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "@mui/material";
import SplashScreen from "./modules/layout/components/SplashScreen";
import TenantNotAvailable from "./modules/layout/components/TenantNotAvailable";
import useTenantInitialization from "./util/useTenantInitilization";
import { getRouterBasename } from "./util";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * The boot tree: what renders while the tenant resolves, and what renders once
 * it has.
 *
 * Extracted from `index.tsx` so the branches can be rendered in a test. The
 * splash branch in particular could not be exercised while it lived in the
 * entry file, which is how it shipped broken.
 */
const MainApp = ({ authInstance }: { authInstance?: any }) => {
  const { theme, isLoading, showSplash, notAvailable, notAvailableReason, notAvailableInfo } =
    useTenantInitialization();

  /**
   * The store wraps EVERY branch, including the two that render before the
   * tenant has resolved.
   *
   * It used to sit below them, so the splash and not-available screens had no
   * store at all. Nothing noticed until `TenantLogo` — which both of them
   * render — grew a `useSelector` for the tenant name (#2136): every boot then
   * threw *"Cannot destructure property 'store' of ... as it is null"* before a
   * single screen appeared.
   *
   * There was never an ordering reason for the Provider to be lower.
   * `baseStore` is a static import, so it exists from the first render.
   *
   * `ThemeProvider` deliberately does NOT move up with it: `theme` is not
   * resolved yet during boot, and the splash relies on MUI's default theme for
   * its spacing.
   */
  return (
    <Provider store={baseStore}>
      {/* Not-available is checked first: an unknown / inactive slug resolves
          quickly, and holding it behind the 2s splash timer would flash the
          default logo before the "not available" screen. */}
      {notAvailable ? (
        <TenantNotAvailable reason={notAvailableReason} info={notAvailableInfo} />
      ) : isLoading || showSplash ? (
        <SplashScreen />
      ) : (
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter basename={getRouterBasename()}>
              <App instance={authInstance} />
            </BrowserRouter>
          </QueryClientProvider>
        </ThemeProvider>
      )}
    </Provider>
  );
};

export default MainApp;
